import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, of, timer } from 'rxjs';
import { catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { HelpConfig } from '../core/config/help-config';
import { UserControllerService } from '../core/services/outros/user-controller.service';
import { LogoutType } from '../core/enums/logout-types.enum';

import {
  MaintenancePhase,
  MaintenanceResponse,
  MaintenanceState,
  pollingIntervalMs,
} from './models/maintenance-state.model';
import { MOCK_ENABLED, MOCK_POLL_INTERVAL_MS, getMockMaintenanceStatus } from './maintenance-mock';

@Injectable({ providedIn: 'root' })
export class MaintenanceService implements OnDestroy {

  private _state$ = new BehaviorSubject<MaintenanceState | null>(null);
  readonly state$: Observable<MaintenanceState | null> = this._state$.asObservable();

  private _destroy$ = new Subject<void>();
  private _forceCheck$ = new Subject<void>();
  private _pollSub: Subscription | null = null;
  private _visibilitySub: (() => void) | null = null;
  private _retryBackoffMs = 5_000;
  private _previousPhase: MaintenancePhase | null = null;
  private _tabHidden = false;

  constructor(
    private http: HttpClient,
    private helpConfig: HelpConfig,
    private userController: UserControllerService,
    private router: Router,
  ) {}

  // ─── API pública ───────────────────────────────────────────────────────────

  start(): void {
    this._scheduleNextPoll(0);
    this._listenVisibility();

    // forceCheck debounced 500ms: absorve múltiplos 503s simultâneos
    this._forceCheck$
      .pipe(debounceTime(500), takeUntil(this._destroy$))
      .subscribe(() => this._scheduleNextPoll(0));
  }

  stop(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._pollSub?.unsubscribe();
    if (this._visibilitySub) this._visibilitySub();
  }

  forceCheck(): void {
    this._forceCheck$.next();
  }

  /** Chamado pelo interceptor quando header X-Maintenance-Phase está presente */
  updatePhaseFromHeader(phase: string): void {
    const current = this._state$.value;
    if (current?.phase !== phase) {
      this.forceCheck();
    }
  }

  currentState(): MaintenanceState | null {
    return this._state$.value;
  }

  // ─── Polling ───────────────────────────────────────────────────────────────

  private _scheduleNextPoll(delayMs: number): void {
    this._pollSub?.unsubscribe();
    this._pollSub = timer(delayMs)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => this._doFetch());
  }

  private _doFetch(): void {
    if (this._tabHidden) return;

    const fetch$: Observable<MaintenanceResponse | null> = MOCK_ENABLED
      ? of(getMockMaintenanceStatus())
      : this.http
          .get<MaintenanceResponse>(`${this.helpConfig.BASE_API}maintenance/status`)
          .pipe(catchError(() => of(null)));

    fetch$.pipe(takeUntil(this._destroy$)).subscribe({
      next: (response) => this._handleResponse(response),
    });
  }

  private _handleResponse(response: MaintenanceResponse | null): void {
    if (!response) {
      // Falha de rede ou JSON inválido — mantém estado, retry com backoff
      this._scheduleNextPoll(this._retryBackoffMs);
      this._retryBackoffMs = Math.min(this._retryBackoffMs * 2, 60_000);
      return;
    }

    this._retryBackoffMs = 5_000; // reset backoff em sucesso

    const now = Date.now();
    const state: MaintenanceState = {
      ...response,
      clientServerOffsetMs: now - Date.parse(response.serverTime),
      receivedAt: now,
    };

    const previousPhase = this._previousPhase;
    this._previousPhase = state.phase;
    this._state$.next(state);

    this._handlePhaseTransition(previousPhase, state.phase);

    const nextInterval = MOCK_ENABLED ? MOCK_POLL_INTERVAL_MS : pollingIntervalMs(state);
    this._scheduleNextPoll(nextInterval);
  }

  private _handlePhaseTransition(from: MaintenancePhase | null, to: MaintenancePhase): void {
    // Entrou em manutenção: logout forçado + navegar para /maintenance
    if (to === 'maintenance' && from !== 'maintenance') {
      // mobile_only: só age se web também estiver em manutenção
      const state = this._state$.value;
      if (state && state.platforms.web.enabled) return;

      // A política de privacidade precisa permanecer pública, inclusive em manutenção.
      if (this.router.url.split('?')[0].startsWith('/politica-de-privacidade')) return;

      this.userController.logout(LogoutType.Unauthorized);
      this.router.navigate(['/maintenance']);
    }
  }

  // ─── Page Visibility ───────────────────────────────────────────────────────

  private _listenVisibility(): void {
    const handler = () => {
      if (document.hidden) {
        this._tabHidden = true;
        this._pollSub?.unsubscribe();
      } else {
        this._tabHidden = false;
        this._scheduleNextPoll(0); // poll imediato ao voltar
      }
    };

    document.addEventListener('visibilitychange', handler);
    this._visibilitySub = () => document.removeEventListener('visibilitychange', handler);
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
